import { useBooleanFlagValue } from "@openfeature/react-sdk";
import { useCallback } from "react";
import { Link, Outlet, useNavigate, useParams } from "react-router";
import { useAuth } from "../context/auth";
import { useEventStream } from "../hooks/use-event-stream";
import { useFetch } from "../hooks/useFetch";
import { Footer } from "./Footer";
import { ImpersonationBanner } from "./ImpersonationBanner";
import { NotificationBell } from "./NotificationBell";
import { NotificationToast } from "./NotificationToast";
import { OfflineBanner } from "./OfflineBanner";
import { OnboardingWizard } from "./OnboardingWizard";
import { SearchBar } from "./SearchBar";

export function AppLayout() {
	const { signOut, hasPermission } = useAuth();
	const { workspaceSlug } = useParams<{ workspaceSlug: string }>();
	const navigate = useNavigate();
	const { data: changelogStatus, loading: changelogLoading } = useFetch<{
		hasUnread: boolean;
	}>(`/api/tenant/${workspaceSlug}/changelog/status`);
	const handleSessionExpired = useCallback(() => {
		navigate("/sign-in", { replace: true });
	}, [navigate]);
	const { events, status } = useEventStream(
		workspaceSlug ?? "",
		handleSessionExpired,
	);
	const webhooksEnabled = useBooleanFlagValue("webhooks-ui", true);

	async function handleSignOut() {
		await signOut();
		navigate("/sign-in", { replace: true });
	}

	function handleWhatsNewClick() {
		fetch(`/api/tenant/${workspaceSlug}/changelog/viewed`, {
			method: "POST",
			credentials: "include",
		});
		window.location.href = "/changelog";
	}

	return (
		<div
			className="min-h-screen flex flex-col bg-gray-50"
			data-sse-status={status}
		>
			<ImpersonationBanner />
			<OfflineBanner />
			<NotificationToast events={events} />
			<header className="bg-white border-b border-gray-200 px-4 py-3 flex items-center justify-between">
				<span className="text-lg font-semibold text-gray-900">Apsu</span>
				{hasPermission("projects", "read") && (
					<SearchBar workspaceSlug={workspaceSlug} />
				)}
				<div className="flex items-center gap-4">
					<button
						type="button"
						onClick={handleWhatsNewClick}
						className="text-sm text-gray-600 hover:text-gray-900 cursor-pointer inline-flex items-center"
					>
						What's New
						{!changelogLoading && changelogStatus?.hasUnread && (
							<span className="w-2 h-2 rounded-full bg-blue-500 inline-block ml-1" />
						)}
					</button>
					{hasPermission("projects", "read") && (
						<Link
							to={`/${workspaceSlug}/projects`}
							className="text-sm text-gray-600 hover:text-gray-900"
						>
							Projects
						</Link>
					)}
					<Link
						to={`/${workspaceSlug}/files`}
						className="text-sm text-gray-600 hover:text-gray-900"
					>
						Files
					</Link>
					<Link
						to={`/${workspaceSlug}/billing`}
						className="text-sm text-gray-600 hover:text-gray-900"
					>
						Billing
					</Link>
					<Link
						to={`/${workspaceSlug}/account`}
						className="text-sm text-gray-600 hover:text-gray-900"
					>
						Account
					</Link>
					{hasPermission("profile", "update") && (
						<Link
							to={`/${workspaceSlug}/settings/team`}
							className="text-sm text-gray-600 hover:text-gray-900"
						>
							Settings
						</Link>
					)}
					{hasPermission("settings", "read") && (
						<Link
							to={`/${workspaceSlug}/settings/notifications`}
							className="text-sm text-gray-600 hover:text-gray-900"
						>
							Notifications
						</Link>
					)}
					{hasPermission("webhooks", "read") && webhooksEnabled && (
						<Link
							to={`/${workspaceSlug}/settings/webhooks`}
							className="text-sm text-gray-600 hover:text-gray-900"
						>
							Webhooks
						</Link>
					)}
					{hasPermission("api-keys", "read") && (
						<Link
							to={`/${workspaceSlug}/settings/api-keys`}
							className="text-sm text-gray-600 hover:text-gray-900"
						>
							API Keys
						</Link>
					)}
					<NotificationBell />
					<Link
						to="/workspaces"
						className="text-sm text-gray-600 hover:text-gray-900"
					>
						Switch workspace
					</Link>
					<button
						type="button"
						onClick={handleSignOut}
						className="text-sm text-gray-600 hover:text-gray-900 cursor-pointer"
					>
						Sign Out
					</button>
				</div>
			</header>
			<main className="flex-1 p-4">
				<Outlet />
			</main>
			<Footer />
			<OnboardingWizard />
		</div>
	);
}
